import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { fetchProductByHandle, formatPrice, getProductVideo, getProductModel3d } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { getDiamondSpecs } from "@/lib/diamondSpecs";
import { Model3dViewer } from "@/components/Model3dViewer";
import {
  Loader2,
  ArrowLeft,
  ShieldCheck,
  Truck,
  RefreshCw,
  FileText,
  ExternalLink,
  Box,
  Play,
  Gem,
} from "lucide-react";

export const Route = createFileRoute("/shop/$handle")({
  loader: async ({ params }) => {
    try {
      const product = await fetchProductByHandle(params.handle);
      return { product };
    } catch {
      return { product: null };
    }
  },
  head: ({ params, loaderData }) => {
    const url = `https://howdendiamonds.com/shop/${params.handle}`;
    const product = loaderData?.product;
    const specs = product ? getDiamondSpecs({ node: product as any }) : null;

    const fallbackTitle = params.handle
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    const rawTitle = product?.title
      ? `${product.title}, HOWDEN`
      : `${fallbackTitle}, HOWDEN`;
    const title =
      rawTitle.length > 60 ? `${rawTitle.slice(0, 57).trimEnd()}…` : rawTitle;

    const specBits = specs
      ? [
          specs.carat ? `${specs.carat.toFixed(2)} ct` : null,
          specs.shape,
          specs.color,
          specs.clarity,
          specs.certLab ? `${specs.certLab}-graded` : null,
        ].filter(Boolean)
      : [];
    const specSummary = specBits.length ? `${specBits.join(" · ")}. ` : "";
    const rawDesc = (product?.description ?? "")
      .replace(/\s+/g, " ")
      .trim();
    const baseDesc = rawDesc
      ? rawDesc
      : "Certified lab-grown diamond, independently graded and offered loose at HOWDEN.";
    const fullDesc = `${specSummary}${baseDesc}`;
    const description =
      fullDesc.length > 160 ? `${fullDesc.slice(0, 157).trimEnd()}…` : fullDesc;
    const heroImage = product?.images?.edges?.[0]?.node?.url;

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "product" },
        ...(heroImage
          ? [
              { property: "og:image", content: heroImage },
              { name: "twitter:image", content: heroImage },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: ProductPage,
});

function ProductPage() {
  const { handle } = Route.useParams();
  const addItem = useCartStore((s) => s.addItem);
  const isLoading = useCartStore((s) => s.isLoading);
  const [activeImage, setActiveImage] = useState(0);
  const ctaRef = useRef<HTMLButtonElement | null>(null);
  const [stickyVisible, setStickyVisible] = useState(false);

  useEffect(() => {
    const el = ctaRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setStickyVisible(!entry.isIntersecting),
      { rootMargin: "0px 0px -40px 0px", threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [handle]);

  const { data: product, isLoading: loading, isError } = useQuery({
    queryKey: ["product", handle],
    queryFn: () => fetchProductByHandle(handle),
  });

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" strokeWidth={1.25} />
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-32 text-center">
        <h1 className="font-serif text-3xl font-light">Stone not found</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          This diamond may have been claimed or moved.
        </p>
        <Link to="/shop" className="mt-8 inline-block border-b border-foreground pb-1 text-xs uppercase tracking-luxe">
          Back to the vault
        </Link>
      </div>
    );
  }

  const variants = product.variants.edges.map((e) => e.node);
  const images = product.images.edges;
  const video = getProductVideo(product as any);
  const model3d = getProductModel3d(product as any);
  type MediaTile =
    | { kind: "image"; url: string; alt: string | null }
    | { kind: "video"; url: string; poster?: string }
    | { kind: "model"; url: string; alt: string | null; poster?: string };
  const mediaTiles: MediaTile[] = [
    ...(model3d ? [{ kind: "model" as const, url: model3d.url, alt: model3d.alt, poster: model3d.poster }] : []),
    ...images.map((img) => ({ kind: "image" as const, url: img.node.url, alt: img.node.altText })),
    ...(video ? [{ kind: "video" as const, url: video.url, poster: images[0]?.node.url }] : []),
  ];

  const initialOptions: Record<string, string> = {};
  for (const opt of product.options) initialOptions[opt.name] = opt.values[0];
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>(initialOptions);

  const variant =
    variants.find((v) =>
      v.selectedOptions.every((o) => selectedOptions[o.name] === o.value),
    ) ?? variants[0];

  const price = variant?.price ?? product.priceRange.minVariantPrice;
  const specs = getDiamondSpecs({ node: product as any });
  const heroImage = images[0]?.node.url;

  const fourCs = [
    { label: "Carat", value: specs.carat ? `${specs.carat.toFixed(2)} ct` : null },
    { label: "Cut / Shape", value: specs.shape },
    { label: "Color", value: specs.color },
    { label: "Clarity", value: specs.clarity },
  ].filter((s) => s.value);

  const jsonLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.title,
    description: product.description,
    image: heroImage ? [heroImage] : undefined,
    sku: variant?.id,
    brand: { "@type": "Brand", name: "HOWDEN" },
    offers: {
      "@type": "Offer",
      price: price.amount,
      priceCurrency: price.currencyCode,
      availability: variant?.availableForSale
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      url: typeof window !== "undefined" ? window.location.href : undefined,
    },
  };

  const handleAdd = async () => {
    if (!variant) return;
    await addItem({
      product: { node: product as any },
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-12 lg:px-12 lg:py-20">
      <Link
        to="/shop"
        className="mb-12 inline-flex items-center gap-2 text-xs uppercase tracking-luxe text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" strokeWidth={1.25} />
        The Diamonds
      </Link>

      <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="relative aspect-square overflow-hidden bg-secondary">
            {(() => {
              const tile = mediaTiles[activeImage];
              if (!tile) {
                return (
                  <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-luxe text-muted-foreground">
                    No image
                  </div>
                );
              }
              if (tile.kind === "image") {
                return (
                  <img
                    src={tile.url}
                    alt={tile.alt ?? product.title}
                    className="h-full w-full object-cover"
                  />
                );
              }
              if (tile.kind === "video") {
                return (
                  <video
                    src={tile.url}
                    poster={tile.poster}
                    controls
                    playsInline
                    className="h-full w-full object-cover"
                  />
                );
              }
              return (
                <>
                  <Model3dViewer src={tile.url} alt={tile.alt} poster={tile.poster} className="h-full w-full" />
                  <span className="pointer-events-none absolute bottom-3 left-3 inline-flex items-center gap-1.5 border border-hairline bg-background/85 px-2 py-1 text-[10px] uppercase tracking-luxe text-foreground backdrop-blur-sm">
                    <Box className="h-3 w-3" strokeWidth={1.5} />
                    Drag to rotate
                  </span>
                </>
              );
            })()}
          </div>
          {mediaTiles.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-3">
              {mediaTiles.map((tile, i) => (
                <button
                  key={`${tile.kind}-${i}`}
                  onClick={() => setActiveImage(i)}
                  className={`relative aspect-square overflow-hidden border ${
                    i === activeImage ? "border-foreground" : "border-hairline"
                  }`}
                  aria-label={tile.kind === "model" ? "View 3D model" : tile.kind === "video" ? "Play video" : "View image"}
                >
                  {tile.kind === "image" && (
                    <img src={tile.url} alt="" className="h-full w-full object-cover" />
                  )}
                  {tile.kind === "video" && (
                    <>
                      {tile.poster && <img src={tile.poster} alt="" className="h-full w-full object-cover" />}
                      <span className="absolute inset-0 flex items-center justify-center bg-foreground/30">
                        <Play className="h-4 w-4 text-background" strokeWidth={1.5} />
                      </span>
                    </>
                  )}
                  {tile.kind === "model" && (
                    <>
                      {tile.poster && <img src={tile.poster} alt="" className="h-full w-full object-cover" />}
                      <span className="absolute inset-0 flex items-center justify-center bg-foreground/30">
                        <Box className="h-4 w-4 text-background" strokeWidth={1.5} />
                      </span>
                    </>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-luxe text-muted-foreground">
            <Gem className="h-3 w-3" strokeWidth={1.5} />
            Lab-Grown · Certified
          </p>
          <h1 className="mt-4 font-serif text-4xl font-light leading-tight lg:text-5xl">
            {product.title}
          </h1>
          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-serif text-3xl">
              {formatPrice(price.amount, price.currencyCode)}
            </span>
            <span className="text-[10px] uppercase tracking-luxe text-muted-foreground">
              loose stone · setting not included
            </span>
          </div>

          {/* The 4Cs — front and centre */}
          {fourCs.length > 0 && (
            <div className="mt-10 border border-hairline">
              <p className="border-b border-hairline px-5 py-3 text-[10px] uppercase tracking-luxe text-muted-foreground">
                The 4Cs
              </p>
              <dl className="grid grid-cols-2 divide-x divide-y divide-hairline">
                {fourCs.map((s) => (
                  <div key={s.label} className="flex flex-col gap-1 p-5">
                    <dt className="text-[10px] uppercase tracking-luxe text-muted-foreground">
                      {s.label}
                    </dt>
                    <dd className="font-serif text-2xl font-light">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {/* Variant selectors */}
          {product.options.length > 0 && variants.length > 1 && (
            <div className="mt-10 space-y-6">
              {product.options.map((opt) => (
                <div key={opt.name}>
                  <p className="text-[10px] uppercase tracking-luxe text-muted-foreground">
                    {opt.name}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {opt.values.map((value) => {
                      const active = selectedOptions[opt.name] === value;
                      const probe = { ...selectedOptions, [opt.name]: value };
                      const candidate = variants.find((v) =>
                        v.selectedOptions.every((o) => probe[o.name] === o.value),
                      );
                      const disabled = candidate ? !candidate.availableForSale : false;
                      return (
                        <button
                          key={value}
                          onClick={() =>
                            setSelectedOptions((prev) => ({ ...prev, [opt.name]: value }))
                          }
                          className={`border px-4 py-2 text-xs uppercase tracking-luxe transition-colors ${
                            active
                              ? "border-foreground bg-foreground text-background"
                              : "border-hairline text-foreground hover:border-foreground"
                          } ${disabled ? "opacity-40 line-through" : ""}`}
                        >
                          {value}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            ref={ctaRef}
            onClick={handleAdd}
            disabled={isLoading || !variant?.availableForSale}
            className="mt-10 flex h-14 w-full items-center justify-center bg-foreground text-xs uppercase tracking-luxe text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : !variant?.availableForSale ? (
              "Claimed"
            ) : (
              "Reserve this stone"
            )}
          </button>

          {(specs.certLab || specs.certNumber || specs.certUrl) && (
            <div className="mt-8 border border-hairline p-5">
              <div className="flex items-start gap-4">
                <FileText className="mt-0.5 h-4 w-4 text-foreground" strokeWidth={1.25} />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-luxe text-muted-foreground">
                    Independent Grading Report
                  </p>
                  <p className="mt-2 font-serif text-lg">
                    {specs.certLab ?? "Certified"}
                    {specs.certNumber ? ` · #${specs.certNumber}` : ""}
                  </p>
                  {specs.certUrl && (
                    <a
                      href={specs.certUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 border-b border-foreground pb-0.5 text-xs uppercase tracking-luxe"
                    >
                      View report <ExternalLink className="h-3 w-3" strokeWidth={1.25} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          <ul className="mt-8 space-y-4 border-t border-hairline pt-8">
            {[
              { icon: ShieldCheck, text: "IGI or GIA graded · 4Cs verified" },
              { icon: Truck, text: "Insured global shipping, fully tracked" },
              { icon: RefreshCw, text: "30-day returns on loose stones" },
            ].map((item) => (
              <li key={item.text} className="flex items-center gap-4 text-sm">
                <item.icon className="h-4 w-4 text-accent-foreground/70" strokeWidth={1.25} />
                <span className="text-muted-foreground">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Description */}
      {product.description && (
        <section className="mt-24 border-t border-hairline pt-16 lg:mt-32 lg:pt-20">
          <div className="grid gap-10 lg:grid-cols-3 lg:gap-16">
            <div>
              <p className="text-xs uppercase tracking-luxe text-muted-foreground">
                About this stone
              </p>
              <h2 className="mt-4 font-serif text-3xl font-light leading-tight lg:text-4xl">
                Read the diamond.
              </h2>
            </div>
            <div className="lg:col-span-2">
              <ul className="space-y-3 text-sm leading-relaxed text-muted-foreground">
                {product.description
                  .split(/\r?\n|[•·|]|(?<=[a-z0-9)])\.\s+(?=[A-Z])/)
                  .map((line) => line.trim())
                  .filter(Boolean)
                  .map((line, i) => (
                    <li key={i} className="border-b border-hairline pb-3 last:border-b-0">
                      {line.replace(/\.$/, "")}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* How it's grown */}
      <section className="mt-20 border-t border-hairline pt-16 lg:mt-28 lg:pt-20">
        <div className="grid gap-10 lg:grid-cols-3 lg:gap-16">
          <div>
            <p className="text-xs uppercase tracking-luxe text-muted-foreground">
              The cultivation
            </p>
            <h2 className="mt-4 font-serif text-3xl font-light leading-tight lg:text-4xl">
              Grown, not mined.
            </h2>
          </div>
          <div className="lg:col-span-2 space-y-5 text-sm leading-relaxed text-muted-foreground">
            <p>
              Every HOWDEN diamond is cultivated in a controlled environment
              over several weeks, atom by atom, identical in chemistry and
              optics to a stone formed underground. The difference is
              traceability, not material.
            </p>
            <p>
              Each rough is then cut, polished and graded against the same
              4C standard as a mined diamond. We list only stones we would
              choose to set ourselves; everything else stays in the rough.
            </p>
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(jsonLd)
            .replace(/</g, "\\u003c")
            .replace(/>/g, "\\u003e")
            .replace(/&/g, "\\u0026")
            .replace(/\u2028/g, "\\u2028")
            .replace(/\u2029/g, "\\u2029"),
        }}
      />

      {/* Sticky CTA bar */}
      <div
        className={`fixed inset-x-0 bottom-0 z-30 border-t border-hairline bg-background/95 backdrop-blur-md transition-transform duration-300 ${
          stickyVisible ? "translate-y-0" : "translate-y-full"
        }`}
        aria-hidden={!stickyVisible}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-3 lg:px-12">
          <div className="flex min-w-0 items-center gap-4">
            {heroImage && (
              <img
                src={heroImage}
                alt=""
                className="hidden h-12 w-12 flex-none object-cover sm:block"
              />
            )}
            <div className="min-w-0">
              <p className="truncate font-serif text-sm leading-tight">
                {product.title}
              </p>
              <p className="truncate text-[10px] uppercase tracking-luxe text-muted-foreground">
                {[specs.shape, specs.carat ? `${specs.carat.toFixed(2)}ct` : null, specs.color, specs.clarity]
                  .filter(Boolean)
                  .join(" · ") || "Lab-grown · Certified"}
              </p>
            </div>
          </div>
          <div className="flex flex-none items-center gap-4">
            <span className="hidden font-serif text-base sm:inline">
              {formatPrice(price.amount, price.currencyCode)}
            </span>
            <button
              onClick={handleAdd}
              disabled={isLoading || !variant?.availableForSale}
              className="flex h-11 items-center justify-center bg-foreground px-6 text-[10px] uppercase tracking-luxe text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : !variant?.availableForSale ? (
                "Claimed"
              ) : (
                "Reserve"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
